import {
  Choice,
  ChoiceValue,
  StepConfig,
  StepConfigProps,
} from "../carAssemblageTypes"
import { SELL_YOUR_CAR_STEPS_PREFIX } from "../stepsConfigForSellYourCar"

// Returns true when a value is found in a set of options
// Value is a simple key value object
// isValueInOptions(
//   {body: 'Hatchback', doors: 5},
//   [{value: {body: 'Hatchback', doors: 5}}, {value: {body: 'Hatchback', doors: 3}}]
// ) === true
// TODO: fix the logic here, it's not correct
// The function checks if all of the keys have values that
// are present in the options, but it should check that those
// values are present in THE SAME option
export const isValueInOptions = (
  value: ChoiceValue,
  options: Choice[]
): boolean => {
  if (value === null) return false
  return Object.keys(value).every((key) => {
    return options.some((option) => option.value[key] === value[key])
  })
}

/**
 * In the body step we sometimes have a special case where doors and body is a combined entry
 * To handle these cases we make sure that both the selected entry and the options in general contains "doors"
 * before we try to validate it that way)
 */
export const isSelectedOptionValidForBodyStep = (
  selected: ChoiceValue,
  options: Choice[]
): boolean => {
  const checkForDoors = (element: Choice) =>
    Object.prototype.hasOwnProperty.call(element.value, "doors")
  const optionsHasDoors = options.some(checkForDoors)

  if (
    Object.prototype.hasOwnProperty.call(selected, "doors") &&
    !optionsHasDoors
  ) {
    const selectedWithoutDoors: ChoiceValue = { body: selected.body! }
    return isValueInOptions(selectedWithoutDoors, options)
  }

  // We don't allow auto select of only doors and no body
  if (
    Object.prototype.hasOwnProperty.call(selected, "doors") &&
    !Object.prototype.hasOwnProperty.call(selected, "body")
  ) {
    return false
  }

  return isValueInOptions(selected, options)
}

/**
 * Checks whether or not the selections are sane and available in the options.
 * If you provide a car to auto fill some data, there may be some mismatches, and we want the user to verify the picks
 */
export const validateSelected = (
  step: StepConfig,
  steps: StepConfig[]
): null | { values: number[]; ruleName: string } | { ruleName: string } => {
  const stepChoices = step.allChoices.concat(step.options || [])
  switch (step.stepName) {
    // @ts-expect-error coming from sell your car steps config
    case "brand":
    case "email_address":
    case "car_condition": // The nested condition attributes are more complex to validate against and we don't gain much by doing so
    case "brand_and_model_name":
      return null
    // @ts-expect-error coming from sell your car steps config
    case "model": {
      const brand = steps[0]?.selected?.brand
      if (!brand) {
        return { ruleName: "valid" }
      }
      const carModel = step.selected.car_model
      const brandModels = (
        steps[0]?.options as NonNullable<
          StepConfigProps["brandAndModelOptions"]
        >
      )?.all.find(([optionBrand]) => optionBrand === brand)?.[1]
      if (!brandModels) {
        throw new Error("Models not found for brand " + brand)
      }
      const modelFound = brandModels.find((model) => model === carModel)

      if (!modelFound) {
        return {
          ruleName: "valid",
        }
      }
      return null
    }
    case "km":
      return parseInt(step.selected.km) >= 1000
        ? null
        : { ruleName: "min", values: [1000] }
    case "equipment":
      return step.options ? null : { ruleName: "unexpected" }
    // @ts-expect-error coming from sell your car steps config
    case `${SELL_YOUR_CAR_STEPS_PREFIX}postcode`:
      return (step.options as ChoiceValue[]).find(
        (option) =>
          option.value === step.selected.value &&
          option.name === step.selected.name
      )
        ? null
        : { ruleName: "unexpected" }
    case "body": {
      // If the selected option have doors, but there are no doors in the available options,
      // we remove the doors from the selected options.
      // This sometimes happens when we auto select options with the data we get from the VIN numbers
      const choices =
        step.completionSource === "initialAssembledCar"
          ? step.options
          : stepChoices
      return isSelectedOptionValidForBodyStep(step.selected, choices)
        ? null
        : { ruleName: "valid" }
    }
    default: {
      const isValid = step.completionSource === "initialAssembledCar"
        ? isValueInOptions(step.selected, step.options) // always strictly validate the external data
        : step.manualInputs?.length
        ? true // skip validation when there are manual inputs
        : isValueInOptions(step.selected, stepChoices) // use all available choices to validate for other completion sources
        
      return isValid ? null : { ruleName: "valid" }
    }
  }
}
